import { formatDamage } from '../../battle/format';
import { affixLines } from '../affixDescriptions';
import { CHANNEL } from '../channel';
import { overlayRoot } from '../columnScale';
import { dieArt } from '../diceArt';
import { rarityName } from '../format';
import { icon } from '../icons';
import { registerEscape } from '../keyboard';
import { getLeaderboard } from '../leaderboardApi';
import type {
  Board,
  Leaderboard,
  LeaderboardApi,
  LeaderboardEntry,
  PseudoResult,
} from '../../telemetry/leaderboard';
import { spellLabel } from '../spellsApi';
import { toast } from '../toast';
import type { DieRecord } from '../../telemetry/types';
import type { Affix, Rarity } from '../../core/types';

/**
 * Panneau des **classements** (phase 7, A87 — `docs/contracts/CONTRAT_A87_UI.md`).
 *
 * Même famille visuelle que les Notes de dev : voile, grand panneau facetté,
 * onglets de pierre (`.f-tabs`). Trois tableaux (chrono de campagne, vague la
 * plus haute — ouvert par défaut —, dégât en un jet), sélecteur de saison, liste
 * de 50 lignes avec la ligne du joueur mise en avant (épinglée hors du top 50),
 * détail dépliant avec les dés Maîtres, lecture en cache hors ligne, retrait.
 *
 * **Jamais bloquant** (A79) : une lecture qui échoue retombe sur la dernière
 * lecture gardée, sans exception ; **anonyme** (A80) : seul le pseudonyme est
 * visible. Le client est lu par `getLeaderboard()` (`api.leaderboard`, sinon le
 * bouturage local).
 */

const BOARDS: { id: Board; label: string }[] = [
  { id: 'chrono', label: 'Chrono de campagne' },
  { id: 'wave', label: 'Vague la plus haute' },
  { id: 'hit', label: 'Dégât en un jet' },
];

/** Tableau ouvert par défaut : la vague la plus haute est le classement majeur. */
export const DEFAULT_BOARD: Board = 'wave';

export const PSEUDO_MIN = 3;
export const PSEUDO_MAX = 16;

/** Messages de refus du pseudonyme, en français. */
export const PSEUDO_MESSAGES: Record<Exclude<PseudoResult, { ok: true }>['reason'], string> = {
  short: `Trop court (${PSEUDO_MIN} caractères au moins)`,
  long: `Trop long (${PSEUDO_MAX} au plus)`,
  chars: 'Caractères non autorisés',
  banned: "Ce pseudonyme n'est pas accepté",
  taken: 'Déjà pris',
  network: 'Impossible de joindre le serveur',
};

const UNAVAILABLE = 'Classements indisponibles : aucun serveur configuré';

// ───────────────────────────────────────────────────────────── Formats (purs)

function esc(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Chrono de campagne : « 1 h 24 min 12 s » (les unités nulles de tête sont omises). */
export function formatChrono(seconds: number, withSeconds = true): string {
  const total = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (!withSeconds) return h > 0 ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min`;
  if (h > 0) return `${h} h ${String(m).padStart(2, '0')} min ${String(s).padStart(2, '0')} s`;
  if (m > 0) return `${m} min ${String(s).padStart(2, '0')} s`;
  return `${s} s`;
}

/** Rang en lettres : « 1er », « 2e », « 112e ». */
export function formatRank(rank: number): string {
  return rank === 1 ? '1er' : `${rank}e`;
}

/** Valeur d'une entrée selon le tableau : chrono, « Niveau 12 · jour 3 » ou dégâts abrégés. */
export function formatEntryValue(board: Board, e: LeaderboardEntry): string {
  if (board === 'chrono') return formatChrono(e.value);
  if (board === 'wave') {
    const level = e.level ?? Math.floor(e.value / 10);
    const day = e.day ?? e.value % 10;
    return `Niveau ${level} · jour ${day}`;
  }
  return formatDamage(e.value);
}

/** Date locale « 13/10 à 15 h 02 » d'un horodatage ISO (« — » si illisible). */
export function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} à ${d.getHours()} h ${p(d.getMinutes())}`;
}

function totalText(n: number): string {
  const v = Math.max(0, Math.floor(n));
  return `${v.toLocaleString('fr-FR')} joueur${v > 1 ? 's' : ''} classé${v > 1 ? 's' : ''}`;
}

const RARITIES: readonly string[] = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'prismatic'];

function safeRarity(r: unknown): Rarity {
  return (typeof r === 'string' && RARITIES.includes(r) ? r : 'common') as Rarity;
}

function safeSides(n: unknown): number {
  return typeof n === 'number' && Number.isFinite(n) && n >= 2 && n <= 1000 ? Math.floor(n) : 6;
}

// ──────────────────────────────────────────────────────────── Lignes et détail

function rankHtml(rank: number): string {
  if (rank >= 1 && rank <= 3) {
    return icon('medal', { className: `lb-medal lb-medal--${rank}`, label: formatRank(rank) });
  }
  return `<span class="num">${rank}</span>`;
}

function diceThumbs(dice: DieRecord[], size: number): string {
  return dice
    .map((d) => dieArt(safeSides(d.sides), safeRarity(d.rarity), { size }))
    .join('');
}

function rowHtml(board: Board, e: LeaderboardEntry, idx: number, pinned = false): string {
  const dice = Array.isArray(e.dice) ? e.dice : [];
  return `
    <li class="lb-item${e.me ? ' lb-item--me' : ''}${pinned ? ' lb-item--pinned' : ''}" data-role="lb-item" data-rank="${e.rank}"${e.me ? ' data-me="true"' : ''}>
      <button type="button" class="lb-row" data-idx="${idx}" aria-expanded="false">
        <span class="lb-rank">${pinned ? `<span class="num">${formatRank(e.rank)}</span>` : rankHtml(e.rank)}</span>
        <span class="lb-main">
          <span class="lb-name" data-role="lb-pseudo">${esc(e.pseudo)}${e.me ? ' <span class="f-badge" data-tone="gold">Vous</span>' : ''}</span>
          <span class="lb-dice" aria-hidden="true">${diceThumbs(dice, 20)}</span>
        </span>
        <span class="lb-value num" data-role="lb-value">${esc(formatEntryValue(board, e))}</span>
      </button>
      <div class="lb-detail" data-role="lb-detail" hidden></div>
    </li>`;
}

/**
 * Face tirée du dé n° `i` : par l'identifiant d'emplacement du Worker
 * (`slot-<emplacement>`, les emplacements vides n'étant pas listés), sinon par
 * numéro d'emplacement, sinon par position.
 */
function faceOf(e: LeaderboardEntry, i: number): { face: number; absorbed: boolean } | null {
  const faces = Array.isArray(e.faces) ? e.faces : [];
  const id = (e.dice[i] as { id?: string } | undefined)?.id;
  return (
    (id ? faces.find((f) => `slot-${f.slot}` === id) : undefined) ??
    faces.find((f) => f.slot === i) ??
    faces[i] ??
    null
  );
}

function detailDie(e: LeaderboardEntry, d: DieRecord, i: number, showFaces: boolean): string {
  const rarity = safeRarity(d.rarity);
  const sides = safeSides(d.sides);
  const name = d.name ? esc(String(d.name)) : `Dé d${sides}`;
  const f = showFaces ? faceOf(e, i) : null;
  const faceBadge = f
    ? f.absorbed
      ? `<span class="lb-face lb-face--absorbed" data-role="lb-face">absorbé</span>`
      : `<span class="lb-face num" data-role="lb-face">${esc(String(f.face))}</span>`
    : '';
  const affixes = affixLines(
    (Array.isArray(d.affixes) ? d.affixes : []).map((a) => ({ id: a.id, value: Number(a.value) })) as unknown as Affix[],
  );
  const spells = (Array.isArray(d.spells) ? d.spells : []).filter((s) => typeof s === 'string');
  return `
    <li class="die-card lb-die" data-rarity="${rarity}" data-role="lb-die">
      <div class="die-card-vig lb-vig">${dieArt(sides, rarity, { size: 44 })}${faceBadge}</div>
      <div class="die-card-main">
        <div class="die-card-head"><span class="die-card-name">${name}</span></div>
        <p class="die-card-badge"><span class="die-card-sides">d${sides}</span><span class="f-badge" data-rarity="${rarity}">${esc(rarityName(rarity))}</span></p>
        ${
          affixes.length
            ? `<ul class="die-card-affixes">${affixes
                .map((a) => `<li>${icon('affix')}<span>${esc(a.name)} <b>${esc(a.value)}</b></span></li>`)
                .join('')}</ul>`
            : ''
        }
        ${
          spells.length
            ? `<ul class="die-card-spells">${spells
                .map((s) => `<li class="die-card-spell">${icon('spell')}<span><span class="die-card-spell-name">${esc(spellLabel(s))}</span></span></li>`)
                .join('')}</ul>`
            : ''
        }
      </div>
    </li>`;
}

function detailHtml(board: Board, e: LeaderboardEntry): string {
  const dice = Array.isArray(e.dice) ? e.dice : [];
  const showFaces = board === 'hit';
  const meta: string[] = [];
  if (e.playSeconds !== undefined && board !== 'chrono') meta.push(`Chrono ${formatChrono(e.playSeconds)}`);
  if (e.recordedAt) meta.push(formatStamp(e.recordedAt));
  let extra = '';
  if (showFaces) {
    const spells = (Array.isArray(e.spells) ? e.spells : []).filter((s) => typeof s === 'string');
    extra = `
      <p class="lb-total" data-role="lb-total">Total du jet : <b class="num">${esc(formatDamage(e.value))}</b></p>
      <p class="lb-spells" data-role="lb-spells">${icon('spell')}<span>${
        spells.length ? `Sorts déclenchés : ${esc(spells.map(spellLabel).join(', '))}` : 'Aucun sort déclenché'
      }</span></p>`;
  }
  return `
    ${meta.length ? `<p class="lb-meta">${esc(meta.join(' · '))}</p>` : ''}
    ${
      dice.length
        ? `<ul class="lb-dice-list">${dice.map((d, i) => detailDie(e, d, i, showFaces)).join('')}</ul>`
        : '<p class="lb-meta">Dés non enregistrés.</p>'
    }
    ${extra}`;
}

// ─────────────────────────────────────────────────────────────────── Panneau

/** Ouvre le panneau des classements par-dessus l'écran courant. */
/**
 * Ouvre le panneau. La télémétrie (et son client des classements) s'installe
 * 250 ms après le montage de l'application, dans un chargement différé : si le
 * client n'est pas encore là, on l'attend jusqu'à 3 s avant de conclure
 * « indisponible ».
 */
export function openLeaderboard(opts: { initial?: Board } = {}): void {
  const first = getLeaderboard();
  if (first) {
    build(first, opts);
    return;
  }
  let tries = 0;
  const timer = window.setInterval(() => {
    const api = getLeaderboard();
    if (api || ++tries >= 30) {
      window.clearInterval(timer);
      build(api, opts);
    }
  }, 100);
}

function build(api: LeaderboardApi | null, opts: { initial?: Board }): void {
  const overlay = document.createElement('div');
  overlay.className = 'f-scrim dev-overlay lb-overlay';
  const available = !!api?.available();
  overlay.innerHTML = `
    <div class="f-modal dev-modal lb-modal" data-role="leaderboard" role="dialog" aria-modal="true" aria-labelledby="lb-title">
      <div class="dev-head">
        <h2 class="dev-title" id="lb-title">${icon('trophy')}<span>Classements</span>${CHANNEL === 'preprod' ? ' <span class="f-badge" data-tone="danger" data-role="lb-preprod">Préprod</span>' : ''}</h2>
        <button type="button" class="f-ibtn" data-action="close" aria-label="Fermer">${icon('close')}</button>
      </div>
      ${
        available
          ? `
      <nav class="f-tabs f-tabs--wrap dev-tabs lb-tabs" role="tablist" aria-label="Classements">
        ${BOARDS.map((b) => `<button type="button" class="f-tab" role="tab" aria-selected="false" data-board="${b.id}">${b.label}</button>`).join('')}
      </nav>
      <div class="lb-bar">
        <label class="lb-season" data-role="lb-season-wrap" hidden><span class="lb-season-label">Saison</span><select class="f-select" data-role="lb-season" aria-label="Saison"></select></label>
        <span class="lb-updated" data-role="lb-updated"></span>
        <button type="button" class="f-btn f-btn--ghost f-btn--sm lb-refresh" data-action="lb-refresh">${icon('revive')}<span>Actualiser</span></button>
      </div>
      <div data-role="lb-status" class="lb-status" role="status"></div>
      <div class="dev-body lb-body" data-role="lb-body" role="tabpanel"></div>
      <div class="lb-foot" data-role="lb-foot"></div>`
          : `
      <div class="dev-body lb-body">
        <p class="f-notice lb-unavailable" data-role="lb-unavailable" role="status">${icon('lock')}<span>${UNAVAILABLE}</span></p>
      </div>`
      }
    </div>`;
  overlayRoot().appendChild(overlay);

  let unregisterEscape = (): void => {};
  let alive = true;
  const close = (): void => {
    alive = false;
    unregisterEscape();
    overlay.remove();
  };
  unregisterEscape = registerEscape(overlay, close);
  overlay.querySelector('[data-action="close"]')?.addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  if (!api || !available) return;

  // ── État ──────────────────────────────────────────────────────────────
  const $ = <T extends HTMLElement>(role: string): T => overlay.querySelector<T>(`[data-role="${role}"]`)!;
  const bodyEl = $<HTMLElement>('lb-body');
  const statusEl = $<HTMLElement>('lb-status');
  const footEl = $<HTMLElement>('lb-foot');
  const updatedEl = $<HTMLElement>('lb-updated');
  const seasonWrap = $<HTMLElement>('lb-season-wrap');
  const seasonSel = $<HTMLSelectElement>('lb-season');
  const tabs = Array.from(overlay.querySelectorAll<HTMLButtonElement>('[data-board]'));

  let board: Board = opts.initial ?? DEFAULT_BOARD;
  let season: string | undefined;
  let seq = 0;
  let shown: LeaderboardEntry[] = [];
  let optedOut = false;
  let removedNotice = false;

  const onPseudo = (): void => {
    optedOut = false;
    removedNotice = false;
    refresh();
  };

  const hasPseudo = (): boolean => !optedOut && !!api.getPseudo();

  const renderFoot = (): void => {
    const pseudo = api.getPseudo();
    footEl.innerHTML = `
      <div class="lb-foot-actions">
        ${hasPseudo() ? `<button type="button" class="f-btn f-btn--ghost f-btn--sm" data-action="lb-change-pseudo">Changer de pseudonyme</button>` : ''}
        <button type="button" class="f-btn f-btn--ghost f-btn--sm" data-action="lb-optout">Me retirer des classements</button>
      </div>
      <p class="lb-anon" data-role="lb-anon">${hasPseudo() && pseudo ? `Vous jouez sous « ${esc(pseudo)} ». ` : ''}Seul votre pseudonyme est visible.</p>`;
    const change = footEl.querySelector<HTMLButtonElement>('[data-action="lb-change-pseudo"]');
    change?.addEventListener('click', () => openPseudoDialog(onPseudo));
    const opt = footEl.querySelector<HTMLButtonElement>('[data-action="lb-optout"]')!;
    let armed: ReturnType<typeof setTimeout> | undefined;
    const disarm = (): void => {
      if (armed !== undefined) clearTimeout(armed);
      armed = undefined;
      opt.dataset.armed = 'false';
      opt.innerHTML = 'Me retirer des classements';
    };
    opt.addEventListener('click', () => {
      if (armed === undefined) {
        // Confirmation en deux touches.
        opt.dataset.armed = 'true';
        opt.innerHTML = 'Confirmer : me retirer';
        armed = setTimeout(disarm, 5000);
        return;
      }
      disarm();
      opt.disabled = true;
      void api.optOut().then(
        (ok) => {
          if (!alive) return;
          opt.disabled = false;
          if (ok) {
            optedOut = true;
            removedNotice = true;
            renderFoot();
            refresh();
          } else {
            toast('Retrait impossible pour le moment.');
          }
        },
        () => {
          if (alive) opt.disabled = false;
          toast('Retrait impossible pour le moment.');
        },
      );
    });
  };

  const renderSeasons = (lb: Leaderboard | null): void => {
    const seasons = lb && Array.isArray(lb.seasons) ? lb.seasons : [];
    seasonWrap.hidden = seasons.length < 2;
    if (seasons.length < 2) return;
    const selected = season ?? lb?.season ?? seasons[0];
    seasonSel.innerHTML = seasons
      .map((s) => `<option value="${esc(s)}"${s === selected ? ' selected' : ''}>Saison ${esc(s)}</option>`)
      .join('');
  };

  const renderBody = (lb: Leaderboard | null, loading: boolean): void => {
    for (const t of tabs) t.setAttribute('aria-selected', String(t.dataset.board === board));
    shown = [];
    let html = '';
    if (removedNotice) {
      html += `<p class="f-notice lb-notice" data-role="lb-removed" role="status">${icon('check')}<span>Vous êtes retiré des classements ; choisissez un nouveau pseudonyme pour y revenir</span></p>`;
    }
    if (!hasPseudo()) {
      html += `<div class="f-notice lb-notice lb-join" data-role="lb-join">${icon('affix')}<span>Choisissez un pseudonyme pour apparaître</span><button type="button" class="f-btn f-btn--primary f-btn--sm" data-action="lb-join">Choisir</button></div>`;
    }
    if (!lb) {
      html += loading
        ? `<p class="lb-empty" data-role="lb-loading" aria-busy="true">Chargement…</p>`
        : '';
    } else if (lb.entries.length === 0) {
      html += `<p class="lb-empty" data-role="lb-empty">Aucun joueur classé pour l'instant.</p>`;
    } else {
      const entries = lb.entries.slice(0, 50);
      shown = entries.slice();
      html += `<ol class="lb-list" data-role="lb-list">${entries.map((e, i) => rowHtml(board, e, i)).join('')}</ol>`;
      const me = lb.me;
      if (me && !entries.some((e) => e.me || e.rank === me.rank)) {
        shown.push(me);
        html += `<div class="lb-gap" aria-hidden="true">···</div><ol class="lb-list lb-list--pinned" data-role="lb-pinned">${rowHtml(board, me, shown.length - 1, true)}</ol>`;
      }
      html += `<p class="lb-total-count" data-role="lb-count">${totalText(lb.total)}</p>`;
    }
    bodyEl.innerHTML = html;
    bodyEl.querySelector('[data-action="lb-join"]')?.addEventListener('click', () => openPseudoDialog(onPseudo));
  };

  const renderStatus = (lb: Leaderboard | null, refreshing: boolean, failed: boolean): void => {
    if (failed || lb?.fromCache) {
      statusEl.innerHTML = lb
        ? `<p class="f-notice lb-offline" data-role="lb-offline">${icon('lock')}<span>Hors ligne : lecture du ${esc(formatStamp(lb.readAt))}</span></p>`
        : `<p class="f-notice lb-offline" data-role="lb-offline">${icon('lock')}<span>Hors ligne : aucune lecture en mémoire</span></p>`;
    } else if (refreshing) {
      statusEl.innerHTML = `<p class="lb-refreshing" data-role="lb-refreshing" aria-busy="true">Actualisation…</p>`;
    } else {
      statusEl.innerHTML = '';
    }
    updatedEl.textContent = lb && !failed && !lb.fromCache && lb.updatedAt ? `Mise à jour : ${formatStamp(lb.updatedAt)}` : '';
  };

  /** Lecture : le cache s'affiche aussitôt, la lecture réseau le remplace. */
  const refresh = (): void => {
    const mine = ++seq;
    const cached = api.cachedBoard(board, season);
    renderSeasons(cached);
    renderBody(cached, true);
    renderStatus(cached, true, false);
    renderFoot();
    let pending: Promise<Leaderboard>;
    try {
      pending = api.fetchBoard(board, season);
    } catch (err) {
      pending = Promise.reject(err);
    }
    pending.then(
      (lb) => {
        if (!alive || mine !== seq) return;
        renderSeasons(lb);
        renderBody(lb, false);
        renderStatus(lb, false, false);
        renderFoot();
      },
      () => {
        if (!alive || mine !== seq) return;
        // Échec : la dernière lecture gardée, avec sa date, jamais d'exception (A79).
        renderBody(cached, false);
        renderStatus(cached, false, true);
      },
    );
  };

  // ── Interactions ──────────────────────────────────────────────────────
  for (const t of tabs) {
    t.addEventListener('click', () => {
      board = (t.dataset.board as Board) ?? DEFAULT_BOARD;
      refresh();
    });
  }
  seasonSel.addEventListener('change', () => {
    season = seasonSel.value || undefined;
    refresh();
  });
  overlay.querySelector('[data-action="lb-refresh"]')?.addEventListener('click', () => refresh());
  bodyEl.addEventListener('click', (ev) => {
    const row = (ev.target as HTMLElement).closest<HTMLButtonElement>('.lb-row');
    if (!row) return;
    const entry = shown[Number(row.dataset.idx)];
    const detail = row.parentElement?.querySelector<HTMLElement>('.lb-detail');
    if (!entry || !detail) return;
    const open = row.getAttribute('aria-expanded') !== 'true';
    row.setAttribute('aria-expanded', String(open));
    if (open && !detail.dataset.built) {
      detail.innerHTML = detailHtml(board, entry);
      detail.dataset.built = 'true';
    }
    detail.hidden = !open;
  });

  refresh();
}

// ───────────────────────────────────────────────────────────── Pseudonyme

/**
 * Dialogue « Votre pseudonyme » : champ de 3 à 16 caractères, compteur, règles
 * en une ligne, « Enregistrer ». Les refus du Worker se lisent en français.
 * `onDone` reçoit le pseudonyme accepté.
 */
export function openPseudoDialog(onDone?: (pseudo: string) => void): void {
  const api: LeaderboardApi | null = getLeaderboard();
  const overlay = document.createElement('div');
  overlay.className = 'f-scrim lb-pseudo-overlay';
  overlay.innerHTML = `
    <form class="f-modal lb-pseudo" data-role="pseudo-dialog" role="dialog" aria-modal="true" aria-labelledby="lb-pseudo-title" novalidate>
      <h2 class="f-modal-title" id="lb-pseudo-title">Votre pseudonyme</h2>
      <label class="lb-pseudo-field">
        <input class="f-input" type="text" data-role="pseudo-input" autocomplete="off" autocapitalize="off" spellcheck="false" aria-label="Pseudonyme" />
        <span class="lb-pseudo-count num" data-role="pseudo-count">0/${PSEUDO_MAX}</span>
      </label>
      <p class="f-modal-text lb-pseudo-rules">${PSEUDO_MIN} à ${PSEUDO_MAX} caractères : lettres, chiffres, espaces, tirets.</p>
      <p class="lb-pseudo-error" data-role="pseudo-error" role="alert" hidden></p>
      <div class="f-modal-actions">
        <button type="button" class="f-btn f-btn--ghost" data-action="pseudo-cancel">Annuler</button>
        <button type="submit" class="f-btn f-btn--primary" data-action="pseudo-save">Enregistrer</button>
      </div>
    </form>`;
  overlayRoot().appendChild(overlay);

  const input = overlay.querySelector<HTMLInputElement>('[data-role="pseudo-input"]')!;
  const count = overlay.querySelector<HTMLElement>('[data-role="pseudo-count"]')!;
  const error = overlay.querySelector<HTMLElement>('[data-role="pseudo-error"]')!;
  const save = overlay.querySelector<HTMLButtonElement>('[data-action="pseudo-save"]')!;
  input.value = api?.getPseudo() ?? '';

  let alive = true;
  let unregisterEscape = (): void => {};
  const close = (): void => {
    alive = false;
    unregisterEscape();
    overlay.remove();
  };
  unregisterEscape = registerEscape(overlay, close);

  const syncCount = (): void => {
    const n = [...input.value.trim()].length;
    count.textContent = `${n}/${PSEUDO_MAX}`;
    count.dataset.over = String(n > PSEUDO_MAX);
  };
  syncCount();
  input.addEventListener('input', () => {
    syncCount();
    error.hidden = true;
  });
  overlay.querySelector('[data-action="pseudo-cancel"]')?.addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  const showError = (text: string): void => {
    error.textContent = text;
    error.hidden = false;
  };

  overlay.querySelector('form')?.addEventListener('submit', (ev) => {
    ev.preventDefault();
    if (!api) {
      showError(PSEUDO_MESSAGES.network);
      return;
    }
    save.disabled = true;
    let pending: Promise<PseudoResult>;
    try {
      pending = api.setPseudo(input.value.trim());
    } catch {
      pending = Promise.resolve({ ok: false, reason: 'network' } as PseudoResult);
    }
    pending.then(
      (res) => {
        if (!alive) return;
        save.disabled = false;
        if (res.ok) {
          close();
          toast(`Pseudonyme enregistré : ${res.pseudo}`);
          onDone?.(res.pseudo);
        } else {
          showError(PSEUDO_MESSAGES[res.reason] ?? PSEUDO_MESSAGES.network);
        }
      },
      () => {
        if (!alive) return;
        save.disabled = false;
        showError(PSEUDO_MESSAGES.network);
      },
    );
  });

  input.focus();
  input.select();
}
